/**
 * EvidenceViewerContext · Wave 392 · 2026-05-14
 *
 * React context + provider for the Evidence Viewer modal.
 *
 * Design doc Sections 4.3 and 8.2.
 *
 * FROZEN INTERFACES — Agent F depends on these shapes.
 * DO NOT change ActiveEvidence or EvidenceViewerContextValue after writing.
 *
 * 2026-05-14 · architect unfreeze: projectId + estimateId added to ActiveEvidence.
 * Removes the document.body.dataset antipattern introduced by Agent E.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from 'react';
import type { ReactNode } from 'react';
import { EvidenceViewerModal } from './EvidenceViewerModal';
import { useEvidencePanelMode, type EvidenceViewMode } from '@/hooks/useEvidencePanelMode';

// ─── ActiveEvidence · Section 4.3 ─────────────────────────────────────────────
// FROZEN — Agent F depends on this shape.

export interface ActiveEvidence {
  projectId: string;   // required — passed directly from the page/chip
  estimateId: string;  // required — passed directly from the page/chip
  docId: string;
  docType: 'pdf' | 'ifc' | 'unknown'; // derived by docType resolver
  page: number | null;
  bbox: [number, number, number, number] | null;
  /** IFC-only: array of 22-char IFC base64 GlobalIds for multi-select highlight. Absent for PDF. */
  globalIds?: string[];
  quote: string;
  rowLabel: string; // e.g. "area_construida_total · 310.6 m²"
}

// ─── EvidenceViewerContextValue · Section 8.2 ────────────────────────────────
// FROZEN — Agent F depends on this shape.

export interface EvidenceViewerContextValue {
  openViewer: (ev: ActiveEvidence, siblings?: ActiveEvidence[]) => void;
  closeViewer: () => void;
  isOpen: boolean;
  current: ActiveEvidence | null;
  siblings: ActiveEvidence[];
  siblingIndex: number;
  goNext: () => void;
  goPrev: () => void;
  /**
   * viewMode: 'panel' on xl+ viewports (>= 1280px); 'modal' on narrow viewports.
   * Defaults to 'modal' on SSR and until the hook fires client-side.
   * Consumers branch on this to render the split panel vs the overlay modal.
   */
  viewMode: EvidenceViewMode;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const EvidenceViewerContext = createContext<EvidenceViewerContextValue | null>(null);

export function useEvidenceViewer(): EvidenceViewerContextValue {
  const ctx = useContext(EvidenceViewerContext);
  if (!ctx) {
    throw new Error('useEvidenceViewer must be used inside <EvidenceViewerProvider>');
  }
  return ctx;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

interface State {
  isOpen: boolean;
  current: ActiveEvidence | null;
  siblings: ActiveEvidence[];
  siblingIndex: number;
}

const INITIAL_STATE: State = {
  isOpen: false,
  current: null,
  siblings: [],
  siblingIndex: 0,
};

export function EvidenceViewerProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>(INITIAL_STATE);
  const viewMode = useEvidencePanelMode();

  const openViewer = useCallback(
    (ev: ActiveEvidence, siblings: ActiveEvidence[] = []) => {
      const idx = siblings.length > 0
        ? siblings.findIndex((s) => s.docId === ev.docId && s.rowLabel === ev.rowLabel)
        : 0;
      setState({
        isOpen: true,
        current: ev,
        siblings: siblings.length > 0 ? siblings : [ev],
        siblingIndex: idx >= 0 ? idx : 0,
      });
    },
    [],
  );

  const closeViewer = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  const goNext = useCallback(() => {
    setState((prev) => {
      if (prev.siblings.length === 0) return prev;
      const nextIdx = (prev.siblingIndex + 1) % prev.siblings.length;
      return {
        ...prev,
        siblingIndex: nextIdx,
        current: prev.siblings[nextIdx] ?? prev.current,
      };
    });
  }, []);

  const goPrev = useCallback(() => {
    setState((prev) => {
      if (prev.siblings.length === 0) return prev;
      const prevIdx = (prev.siblingIndex - 1 + prev.siblings.length) % prev.siblings.length;
      return {
        ...prev,
        siblingIndex: prevIdx,
        current: prev.siblings[prevIdx] ?? prev.current,
      };
    });
  }, []);

  // In panel mode, opening still sets isOpen=true for keyboard nav consistency.
  // Closing in panel mode clears `current` → panel shows empty state.
  // The modal short-circuits when viewMode==='panel' (see EvidenceViewerModal).

  // Keyboard navigation: Escape → close, Arrow keys → navigate evidence
  useEffect(() => {
    if (!state.isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeViewer();
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [state.isOpen, closeViewer, goNext, goPrev]);

  const value = useMemo<EvidenceViewerContextValue>(
    () => ({
      openViewer,
      closeViewer,
      isOpen: state.isOpen,
      current: state.current,
      siblings: state.siblings,
      siblingIndex: state.siblingIndex,
      goNext,
      goPrev,
      viewMode,
    }),
    [openViewer, closeViewer, state, goNext, goPrev, viewMode],
  );

  return (
    <EvidenceViewerContext.Provider value={value}>
      {children}
      <EvidenceViewerModal />
    </EvidenceViewerContext.Provider>
  );
}
